/**
 * `/pricing`: every active plan, priced from `GET /subscriptions/plans`, so a
 * price changed in the billing console changes here. Shown inc GST (Rob,
 * 2026-10-09); the plans are stored ex GST.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PublicLayout from '@/components/public/PublicLayout';
import { FEATURES, featureForPlan } from '@/content/features';
import { usePublicPlans } from '@/hooks/usePublicPlans';
import { activeTiers, estimateMonthly, formatPrice, incGst } from '@/utils/publicPricing';
import type { SubscriptionPlan } from '@/types/subscription';
import { cn } from '@/utils/cn';

/** Plans in the order the site lists their features; any without a page last. */
function orderPlans(plans: SubscriptionPlan[]): SubscriptionPlan[] {
  const rank = (p: SubscriptionPlan) => {
    const i = FEATURES.findIndex((f) => f.planKey === p.module_key);
    return i === -1 ? FEATURES.length : i;
  };
  return plans.slice().sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

function PlanCard({ plan }: { plan: SubscriptionPlan }) {
  const feature = featureForPlan(plan.module_key);
  const base = incGst(plan.base_price);
  const tiers = activeTiers(plan);
  return (
    <div className="flex min-w-0 flex-col rounded-2xl border border-neutral-200 bg-white p-6">
      <div className="flex items-center gap-3">
        {feature && (
          <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', feature.tone)}>
            <feature.icon className="h-5 w-5" />
          </span>
        )}
        <h3 className="min-w-0 text-lg font-semibold text-neutral-900">{feature?.name ?? plan.name}</h3>
      </div>
      {feature && <p className="mt-3 text-sm leading-relaxed text-neutral-600">{feature.summary}</p>}
      <p className="mt-5">
        {base > 0 ? (
          <>
            <span className="text-3xl font-bold text-neutral-900">{formatPrice(base)}</span>
            <span className="text-neutral-600"> a month</span>
          </>
        ) : (
          <span className="text-lg font-semibold text-neutral-900">No base fee</span>
        )}
      </p>
      {tiers.length > 0 && (
        <ul className="mt-4 flex-1 space-y-2 border-t border-neutral-100 pt-4 text-sm">
          {tiers.map((t) => {
            const p = incGst(t.price_per_seat);
            return (
              <li key={t.id} className="flex justify-between gap-4">
                <span className="min-w-0 text-neutral-700">{t.name} seat</span>
                <span className={cn('shrink-0 font-medium', p > 0 ? 'text-neutral-900' : 'text-emerald-700')}>
                  {p > 0 ? `${formatPrice(p)}/month` : 'Free'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {feature?.freeNote && <p className="mt-4 text-xs text-neutral-500">{feature.freeNote}</p>}
      {feature && (
        <Link to={`/features/${feature.slug}`} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-800">
          What it does <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}

function Estimator({ plans }: { plans: SubscriptionPlan[] }) {
  const [chosen, setChosen] = useState<Record<number, boolean>>({});
  const [seats, setSeats] = useState<Record<number, Record<number, number>>>({});

  const total = useMemo(
    () => estimateMonthly(plans.filter((p) => chosen[p.id]).map((p) => ({ plan: p, seats: seats[p.id] ?? {} }))),
    [plans, chosen, seats]
  );

  const setSeat = (planId: number, tierId: number, value: string) => {
    const n = Math.max(0, Math.min(999, Math.floor(Number(value) || 0)));
    setSeats((s) => ({ ...s, [planId]: { ...(s[planId] ?? {}), [tierId]: n } }));
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_20rem]">
      <ul className="divide-y divide-neutral-200 rounded-2xl border border-neutral-200 bg-white">
        {plans.map((plan) => {
          const name = featureForPlan(plan.module_key)?.name ?? plan.name;
          const paid = activeTiers(plan).filter((t) => incGst(t.price_per_seat) > 0);
          const on = !!chosen[plan.id];
          return (
            <li key={plan.id} className="p-4">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => setChosen((c) => ({ ...c, [plan.id]: e.target.checked }))}
                  className="h-5 w-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="min-w-0 flex-1 font-medium text-neutral-900">{name}</span>
                {incGst(plan.base_price) > 0 && (
                  <span className="shrink-0 text-sm text-neutral-600">{formatPrice(incGst(plan.base_price))}</span>
                )}
              </label>
              {on && paid.length > 0 && (
                <div className="ml-8 mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {paid.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm text-neutral-700">
                      <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={seats[plan.id]?.[t.id] ?? 0}
                        onChange={(e) => setSeat(plan.id, t.id, e.target.value)}
                        className="w-20 rounded-lg border border-neutral-300 px-2 py-1"
                        aria-label={`${name}: ${t.name} seats`}
                      />
                      <span className="min-w-0">
                        {t.name} at {formatPrice(incGst(t.price_per_seat))}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="h-fit rounded-2xl bg-neutral-900 p-6 text-white lg:sticky lg:top-24">
        <p className="text-sm text-neutral-400">Estimated monthly cost</p>
        <p className="mt-2 text-4xl font-bold" aria-live="polite">{formatPrice(total)}</p>
        <p className="mt-2 text-xs text-neutral-400">Inc GST. Free seats and free features are not counted.</p>
        <Link to="/register" className="mt-6 block rounded-xl bg-white px-4 py-3 text-center font-semibold text-neutral-900 hover:bg-neutral-100">
          Get started free
        </Link>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { plans, loading } = usePublicPlans();
  const ordered = useMemo(() => orderPlans(plans), [plans]);
  const included = FEATURES.filter((f) => !f.planKey);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Pricing | FactreeTech';
    return () => {
      document.title = 'FactreeTech';
    };
  }, []);

  return (
    <PublicLayout>
      <section className="bg-gradient-to-b from-primary-50 via-white to-white">
        <div className="mx-auto max-w-3xl space-y-4 px-4 pb-10 pt-14 text-center sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">Pay for what you switch on</h1>
          <p className="text-lg text-neutral-600">
            Signing up is free. Each module has a monthly price, and seats for the people who run it. Everyone else in
            your organisation uses the free parts at no cost. All prices are in Australian dollars and include GST.
          </p>
        </div>
      </section>

      <section className="bg-white pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <p className="py-10 text-center text-neutral-500">Loading prices…</p>
          ) : ordered.length === 0 ? (
            <p className="py-10 text-center text-neutral-600">
              Prices could not be loaded just now. Please try again shortly, or{' '}
              <Link to="/register" className="font-semibold text-primary-700">create a free account</Link> to see them.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {ordered.map((p) => (
                <PlanCard key={p.id} plan={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-neutral-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-neutral-900 sm:text-3xl">Included with every account</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {included.map((f) => (
              <Link key={f.slug} to={`/features/${f.slug}`} className="min-w-0 rounded-2xl border border-neutral-200 bg-white p-5 hover:border-primary-300">
                <span className={cn('flex h-10 w-10 items-center justify-center rounded-xl', f.tone)}>
                  <f.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-3 font-semibold text-neutral-900">{f.name}</h3>
                {f.freeNote && <p className="mt-1 text-sm text-neutral-600">{f.freeNote}</p>}
              </Link>
            ))}
            <div className="min-w-0 rounded-2xl border border-neutral-200 bg-white p-5">
              <ul className="space-y-2 text-sm text-neutral-700">
                {['Clocking in and out, and requesting leave, for every staff member', 'The Android app'].map((l) => (
                  <li key={l} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {ordered.length > 0 && (
        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-neutral-900 sm:text-3xl">Estimate your monthly cost</h2>
            <p className="mb-8 mt-2 text-neutral-600">Tick the modules you want and enter how many people need each paid seat.</p>
            <Estimator plans={ordered} />
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
